import { useEffect } from "react";

const BASE_TITLE = "MYNA AI";
const BASE_URL = "https://myna-ai.co";
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

interface PageMeta {
  /** Page title (appended with "| MYNA AI") */
  title: string;
  /** Meta description for search engines (max ~155 chars) */
  description: string;
  /** Canonical URL path (e.g. "/pricing") */
  path?: string;
  /** OG image URL override (defaults to site OG image) */
  image?: string;
}

function setMeta(name: string, content: string, attr = "name") {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Sets document title, meta description, OG tags, and canonical URL.
 * Restores previous values on unmount (SPA-friendly).
 */
export function usePageMeta({ title, description, path, image }: PageMeta) {
  useEffect(() => {
    const prevTitle = document.title;
    const prevDesc = document.querySelector('meta[name="description"]')?.getAttribute("content") ?? "";
    const prevOgTitle = document.querySelector('meta[property="og:title"]')?.getAttribute("content") ?? "";
    const prevOgDesc = document.querySelector('meta[property="og:description"]')?.getAttribute("content") ?? "";
    const prevOgUrl = document.querySelector('meta[property="og:url"]')?.getAttribute("content") ?? "";
    const prevOgImage = document.querySelector('meta[property="og:image"]')?.getAttribute("content") ?? "";
    const prevTwTitle = document.querySelector('meta[name="twitter:title"]')?.getAttribute("content") ?? "";
    const prevTwDesc = document.querySelector('meta[name="twitter:description"]')?.getAttribute("content") ?? "";
    const prevTwImage = document.querySelector('meta[name="twitter:image"]')?.getAttribute("content") ?? "";
    const prevCanonical = document.querySelector('link[rel="canonical"]')?.getAttribute("href") ?? "";

    const fullTitle = `${title} | ${BASE_TITLE}`;
    const canonicalUrl = path ? `${BASE_URL}${path}` : BASE_URL;
    const ogImage = image ?? DEFAULT_IMAGE;

    // Set all meta
    document.title = fullTitle;
    setMeta("description", description);
    setMeta("og:title", fullTitle, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", canonicalUrl, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("twitter:title", fullTitle);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);
    setCanonical(canonicalUrl);

    return () => {
      document.title = prevTitle;
      setMeta("description", prevDesc);
      setMeta("og:title", prevOgTitle, "property");
      setMeta("og:description", prevOgDesc, "property");
      setMeta("og:url", prevOgUrl, "property");
      setMeta("og:image", prevOgImage, "property");
      setMeta("twitter:title", prevTwTitle);
      setMeta("twitter:description", prevTwDesc);
      setMeta("twitter:image", prevTwImage);
      setCanonical(prevCanonical);
    };
  }, [title, description, path, image]);
}
